import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail, BadgeCheck, Languages, User } from "lucide-react";
import { useHome } from "@/i18n/home";
import { CONTACT, COMPANY } from "@/i18n/translations";

const Photo = ({ alt }) => {
  const [ok, setOk] = useState(true);
  return (
    <div className="relative h-40 w-40 shrink-0 overflow-hidden rounded-[2rem] bg-[#0A0E13] ring-1 ring-[#00C3FF]/30 sm:h-48 sm:w-48" data-testid="person-photo">
      {ok ? (
        <img src="/otto.jpg" alt={alt} className="h-full w-full object-cover" onError={() => setOk(false)} />
      ) : (
        <div className="grid h-full w-full place-items-center" style={{ background: "radial-gradient(circle at 30% 20%, rgba(0,195,255,0.35), transparent 60%), radial-gradient(circle at 80% 90%, rgba(255,122,0,0.3), transparent 55%), #0A0E13" }}>
          <User size={64} color="rgba(255,255,255,0.75)" strokeWidth={1.4} />
        </div>
      )}
    </div>
  );
};

const Fact = ({ icon: Icon, children, id }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-sm text-white/85" data-testid={`person-fact-${id}`}>
    <Icon size={15} color="#00C3FF" /> {children}
  </span>
);

const Btn = ({ href, icon: Icon, label, id, cls }) => (
  <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={`person-${id}`}
    className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-transform hover:scale-[1.03] ${cls}`}>
    <Icon size={16} /> {label}
  </a>
);

export const ContactPerson = () => {
  const p = useHome().person;
  return (
    <section className="relative bg-[#F7F8FA] pb-24 sm:pb-28" data-testid="contact-person">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-col gap-8 overflow-hidden rounded-[2rem] bg-[#090A0C] p-7 sm:flex-row sm:items-center sm:p-10">
          <span className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#00C3FF]/20 blur-3xl" aria-hidden="true" />
          <Photo alt={COMPANY.owner} />
          <div className="relative min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00C3FF]">{p.overline}</p>
            <h3 className="mt-3 font-display text-3xl font-semibold text-white sm:text-4xl" data-testid="person-name">{COMPANY.owner}</h3>
            <p className="mt-1 text-base font-medium text-[#FF7A00]" data-testid="person-role">{p.role}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Fact icon={User} id="age">{p.age}</Fact>
              <Fact icon={BadgeCheck} id="cert">UniFi Wireless Administrator</Fact>
              <Fact icon={Languages} id="lang">{p.langs}</Fact>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Btn href={`tel:${CONTACT.phoneRaw}`} icon={Phone} label={CONTACT.phone} id="call" cls="bg-[#00C3FF] text-[#090A0C]" />
              <Btn href={`https://wa.me/${CONTACT.whatsapp}`} icon={MessageCircle} label="WhatsApp" id="whatsapp" cls="bg-[#25D366] text-white" />
              <Btn href={`mailto:${CONTACT.email}`} icon={Mail} label={p.mail} id="mail" cls="border border-white/20 text-white" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
